import { AgnesTooltip } from '@agnes/ui';
import { HelpCircle } from 'lucide-react';
import {
  matrizExposicao,
  matrizImpactos,
  matrizLegendas,
  matrizPrioridades,
  RISCO_COR,
  RISCO_LABEL,
} from '@/mocks/trabalhador';
import { qaKey } from '@/lib/dataQa';

interface MatrizExposicaoProps {
  /** Prefixo dos data-qa internos (grade, células, eixos e legenda). */
  dataQa: string;
  /** data-qa do contêiner; por padrão, o próprio prefixo. */
  rootDataQa?: string;
}

// data-qa só aceita ASCII: "Médio"/"Crítico" viram "medio"/"critico"
const impactoQaKey = (impacto: string) =>
  qaKey(impacto.normalize('NFD').replace(/[\u0300-\u036f]/g, ''));

// matriz de exposição (heatmap Prioridade × Impacto + legenda) — referência estática do legado
export function MatrizExposicao({ dataQa, rootDataQa = dataQa }: MatrizExposicaoProps) {
  return (
    <div
      data-qa={rootDataQa}
      role="group"
      aria-label="Matriz de exposição por prioridade e impacto"
      className="grid grid-cols-1 gap-6 lg:grid-cols-5"
    >
      <div data-qa={`${dataQa}-grade`} className="flex lg:col-span-3">
        <div className="flex items-center">
          <span className="rotate-180 bg-agnes-bg-muted px-1 py-2 text-xs text-agnes-text-muted [writing-mode:vertical-rl]">
            Prioridade
          </span>
        </div>
        <div className="flex-1">
          <div className="grid grid-cols-[56px_repeat(4,1fr)] overflow-hidden rounded-md text-xs">
            {matrizExposicao.map((row, rowIndex) => (
              <div key={matrizPrioridades[rowIndex]} className="contents">
                <div className="flex items-center justify-center bg-agnes-bg-muted px-1 py-8 text-agnes-text-muted">
                  {matrizPrioridades[rowIndex]}
                </div>
                {row.map((cell, cellIndex) => (
                  <div
                    key={`${rowIndex}-${cellIndex}`}
                    data-qa={`${dataQa}-celula-${rowIndex}-${cellIndex}`}
                    className="flex items-center justify-center px-1 py-8 text-center"
                    style={{ background: RISCO_COR[cell].bg, color: RISCO_COR[cell].fg }}
                  >
                    {RISCO_LABEL[cell]}
                  </div>
                ))}
              </div>
            ))}
            <div className="bg-agnes-bg-muted" />
            {matrizImpactos.map((impacto) => (
              <div
                key={impacto}
                data-qa={`${dataQa}-impacto-${impactoQaKey(impacto)}`}
                className="flex items-center justify-center bg-agnes-bg-muted px-1 py-2 text-center text-agnes-text-muted"
              >
                {impacto}
              </div>
            ))}
          </div>
          <div className="mt-1 bg-agnes-bg-muted py-1 text-center text-xs text-agnes-text-muted">
            Impacto
          </div>
        </div>
      </div>

      <ul data-qa={`${dataQa}-legenda`} className="space-y-2 text-sm lg:col-span-2">
        {matrizLegendas.map((item) => (
          <li
            key={item.nivel}
            data-qa={`${dataQa}-legenda-${qaKey(item.nivel)}`}
            className="rounded-md border-l-4 p-2"
            style={{
              borderColor: RISCO_COR[item.nivel].bg,
              background: `${RISCO_COR[item.nivel].bg}12`,
            }}
          >
            <p className="flex items-center gap-1 font-semibold text-agnes-text">
              {RISCO_LABEL[item.nivel]}
              <AgnesTooltip
                className="max-w-xs whitespace-normal"
                content={item.tooltip}
                dataQa={`${dataQa}-legenda-${qaKey(item.nivel)}-tooltip`}
              >
                <HelpCircle
                  className="h-3.5 w-3.5 text-agnes-text-muted"
                  aria-label={`Mais informações sobre risco ${RISCO_LABEL[item.nivel]}`}
                />
              </AgnesTooltip>
            </p>
            <p className="text-agnes-text-muted">{item.texto}</p>
          </li>
        ))}
      </ul>
    </div>
  );
}
